import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Heart } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../Card.tsx";
import { Badge } from "../Badge.js";
import ReactWordcloud from "react-wordcloud";
import { motion, AnimatePresence } from "framer-motion";
import { decryptfromHEX } from "../../routes/encodedecode.js";

interface CommentCardProps {
  comment: SocialGlassModels.TopComments;
}

const formatLikes = (likes: number) => {
  if (likes >= 1000000) return `~${(likes / 1000000).toFixed(1)}M`;
  if (likes >= 1000) return `~${(likes / 1000).toFixed(1)}K`;
  return likes.toString();
};

const CommentCard = ({ comment }: CommentCardProps) => (
  <div className="bg-white-50 m-0 flex h-full flex-col justify-between rounded-2xl border border-gray-300 px-8 py-3 shadow-[0px_0px_10px_rgba(0,0,0,0.1)] transition-shadow duration-200">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <h3 className="mb-1 text-left text-[14px] font-semibold text-black">
          @{comment.username}
        </h3>
        {comment.commentText && (
          <p className="m-0 text-left text-[14px] font-normal leading-snug text-[#1F263E]">
            {comment.commentText}
          </p>
        )}
      </div>
      <div className="ml-4 flex flex-col items-center gap-2">
        <Heart className="h-4 w-auto fill-red-500 text-red-500" />
        <span className="text-[12px] font-normal text-black">
          {formatLikes(comment.likes)}
        </span>
      </div>
    </div>

    {/* Push this to the bottom */}
    <div className="mt-0 flex justify-end">
      <a
        href={comment.viewSource}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[10px] font-normal text-[#ED2C08]"
      >
        View Source
      </a>
    </div>
  </div>
);

const App = () => {
  const [phrases, setPhrases] = useState<SocialGlassModels.phrase[]>([]);
  const [topComments, setTopComments] = useState<
    SocialGlassModels.TopComments[]
  >([]);
  const [selectedPhrase, setSelectedPhrase] =
    useState<SocialGlassModels.phrase["phrase"]>(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const encrypted = queryParams.get("p");
  let orgID: string = "";
  if (encrypted) {
    try {
      const decrypted = decryptfromHEX(encrypted);
      const parsed = JSON.parse(decrypted);
      orgID = parsed.orgID;
      console.log("Decrypted orgID:", orgID);
    } catch (err) {
      console.error("Failed to decrypt orgID from URL:", err);
    }
  }

  useEffect(() => {
    if (!orgID) return;
    fetch(
      `https://genz-staging.feeltiptop.com/api/user/get_report?p=${orgID}&section=Comments`,
    )
      .then((res) => res.json())
      .then((data: SocialGlassModels.Comments) => {
        console.log("how the data looks like for comments: ", data);
        const keyLingo: SocialGlassModels.phrase[] =
          data?.KeyLingoAndVerbiage?.phrases || [];
        setPhrases(keyLingo);

        //set the comments - type check
        setTopComments(data?.TopCommentsOverall || []);
        if (keyLingo.length > 0) setSelectedPhrase(keyLingo[0].phrase);
      })
      .catch(() => {
        setPhrases([]);
        setTopComments([]);
      });
  }, [orgID]);

  const selectedPhraseData = phrases.find((p) => p.phrase === selectedPhrase);

  return (
    <AnimatePresence>
      <motion.div
        key="comment"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        transition={{ duration: 0.4 }}
        style={{ fontFamily: "Acumin Variable Concept, sans-serif" }}
      >
        <div className="bg-white">
          <div className="mx-4 max-w-7xl px-4 py-8">
            <div className="mb-4 h-full">
              <h1 className="mb-0 ml-0 text-left text-[20px] font-bold text-black">
                Top Comments Overall
              </h1>
            </div>
            <div className="grid max-h-96 grid-cols-1 gap-x-12 gap-y-6 overflow-y-scroll p-2 [-ms-overflow-style:none] [scrollbar-width:none] md:grid-cols-2 [&::-webkit-scrollbar]:hidden">
              {topComments.map(
                (comment: SocialGlassModels.TopComments, idx) => (
                  <CommentCard
                    key={comment.commentId || idx}
                    comment={comment}
                  />
                ),
              )}
            </div>
          </div>
        </div>

        <div className="mx-2 max-w-7xl px-4 py-8">
          {/* Header */}
          <div className="mt-4 bg-white px-0 py-0">
            <div className="mx-auto">
              <h1 className="mb-0 ml-4 text-left text-[20px] font-bold text-black">
                Key Lingo & Verbiage
              </h1>
            </div>
          </div>
          <div className="mx-3 mt-4 flex h-[600px] flex-col rounded-2xl bg-white p-6 shadow-[0_2px_10px_rgba(0,0,0,0.15)] lg:flex-row">
            {/* Left Panel - Phrase Cloud */}
            <div className="h-full overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] lg:w-1/2 [&::-webkit-scrollbar]:hidden">
              <Card>
                <CardContent>
                  <div style={{ height: 500, width: "100%" }}>
                    <ReactWordcloud
                      words={phrases
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 30)
                        .map((phrase) => ({
                          text: phrase.phrase,
                          value: phrase.count,
                        }))}
                      options={{
                        rotations: 0,
                        rotationAngles: [0, 0],
                        fontSizes: [16, 50],
                        deterministic: true,
                        enableTooltip: false,
                        fontFamily: "Acumin Variable Concept",
                        fontStyle: "normal",
                        fontWeight: "700",
                        transitionDuration: 0,
                      }}
                      callbacks={{
                        getWordColor: (word: any) => {
                          if (word.text === selectedPhrase) return "#DB4225";
                          const maxCount = Math.max(
                            ...phrases.map((p) => p.count || 0),
                            1,
                          );
                          return word.value / maxCount > 0.5
                            ? "#8D8D8D"
                            : "#C0C0C0";
                        },
                        onWordClick: (word: any) => {
                          setSelectedPhrase(word.text);
                        },
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Panel - Comments */}
            <div className="h-full lg:w-1/2">
              <Card className="sticky top-4 h-full">
                <CardHeader>
                  <CardTitle>
                    {selectedPhrase ? (
                      <div className="text-left">
                        <span
                          style={{
                            fontFamily: "'Acumin Variable Concept', sans-serif",
                            fontStyle: "normal",
                            fontWeight: 700,
                            fontSize: "24px",
                            lineHeight: "29px",
                            color: "#000000",
                          }}
                        >
                          "{selectedPhrase}"
                        </span>
                        <Badge variant="secondary" className="ml-2">
                          seen {selectedPhraseData?.count} times
                        </Badge>
                        <p className="mt-2 text-[16px] font-normal text-black">
                          Expression of high praise
                        </p>
                      </div>
                    ) : (
                      "Select a phrase to see comments"
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-[400px] space-y-4 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {selectedPhraseData?.associatedComments?.map(
                      (comment: SocialGlassModels.Comment, index: number) => (
                        <div
                          key={index}
                          className="bg-white-50 border-1 m-3 rounded-2xl border-gray-300 p-3 shadow-[0px_0px_10px_rgba(0,0,0,0.1)] transition-shadow duration-200"
                        >
                          <div className="mb-0 flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="mb-0 text-left text-[14px] font-semibold text-black">
                                {comment.username}
                              </h3>
                              {comment.commentText && (
                                <p className="mb-0 text-left text-[14px] font-normal leading-snug text-[#1F263E]">
                                  {comment.commentText}
                                </p>
                              )}
                            </div>
                            <div className="ml-4 flex flex-col items-center gap-2">
                              <Heart className="h-4 w-auto fill-red-500 text-red-500" />
                              <span className="text-[12px] font-normal text-black">
                                {formatLikes(comment.likes)}
                              </span>
                            </div>
                          </div>
                          <div className="mt-0 flex justify-end">
                            <a
                              href={comment.viewSource}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] font-normal text-[#ED2C08]"
                            >
                              View Source
                            </a>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default App;
